import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Compass, Library, Search, Users, Trophy, Target, Coffee, LogOut, ChevronLeft, Menu, Globe, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { UserAvatar } from "./UserAvatar";
import { NotificationBell } from "./NotificationBell";
import { useTheme } from "./ThemeProvider";
import { useTranslation } from "../i18n";
import axios from "axios";
import { toast } from "sonner";

const API = '/api';

const navItemDefs = [
  { icon: Compass, key: "dashboard", path: "/dashboard" },
  { icon: Library, key: "library", path: "/library" },
  { icon: Search, key: "search", path: "/search" },
  { icon: Users, key: "discover", path: "/feed" },
  { icon: Trophy, key: "achievements", path: "/achievements" },
  { icon: Target, key: "challenges", path: "/challenges" },
];

export function Sidebar({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const { theme } = useTheme();
  const { t, lang, setLang } = useTranslation();
  const isDark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  const horizontalLogo = isDark ? "/logo-horizontal-dark.svg" : "/logo-horizontal.svg";

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) { /* ignore */ }
    localStorage.removeItem("session_token");
    toast.success(t.nav.loggedOut);
    navigate("/");
  };

  const toggleLang = () => setLang(lang === "es" ? "en" : "es");

  return (
    <aside
      className={`hidden lg:flex fixed left-0 top-0 bottom-0 flex-col bg-card border-r border-border z-50 transition-all duration-300 ease-in-out ${collapsed ? "w-[72px]" : "w-64"}`}
      data-testid="sidebar"
    >
      {/* Header with logo */}
      <div className="p-4 pb-2">
        <div className="flex items-center justify-between">
          {!collapsed ? (
            <Link to="/dashboard" className="flex items-center min-w-0" data-testid="sidebar-logo">
              <img src={horizontalLogo} alt="AdventureTrack!" className="h-6 object-contain" />
            </Link>
          ) : (
            <Link to="/dashboard" className="mx-auto" data-testid="sidebar-logo">
              <img src="/favicon.svg" alt="AdventureTrack!" className="w-7 h-7 object-contain" />
            </Link>
          )}
          <Button variant="ghost" size="icon" onClick={() => setCollapsed(!collapsed)} className="w-8 h-8 text-muted-foreground hover:text-foreground flex-shrink-0" data-testid="sidebar-toggle">
            {collapsed ? <Menu className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-y-auto">
        {navItemDefs.map((item) => {
          const isActive = location.pathname === item.path;
          const label = t.nav[item.key] || item.key;
          return (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.path.slice(1)}`}
              title={collapsed ? label : undefined}
              className={`flex items-center gap-3 rounded-lg transition-colors ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"} ${
                isActive
                  ? "bg-primary/10 text-primary font-medium"
                  : "text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              <item.icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActive ? 2.2 : 1.8} />
              {!collapsed && <span className="text-sm truncate">{label}</span>}
            </Link>
          );
        })}

        {/* Ko-fi link */}
        <a
          href="https://ko-fi.com/adventuretrack"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="kofi-link"
          title={collapsed ? "Ko-fi" : undefined}
          className={`flex items-center gap-3 rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"}`}
        >
          <Coffee className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
          {!collapsed && <span className="text-sm truncate">Ko-fi</span>}
        </a>

        {/* Admin link (only for admins) */}
        {user?.role === "admin" && (
          <Link
            to="/admin"
            data-testid="nav-admin"
            title={collapsed ? "Admin" : undefined}
            className={`flex items-center gap-3 rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"} ${
              location.pathname === "/admin" ? "bg-primary/10 text-primary font-medium" : ""
            }`}
          >
            <Shield className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
            {!collapsed && <span className="text-sm truncate">Admin</span>}
          </Link>
        )}
      </nav>

      {/* Bottom: Lang toggle + User + Logout */}
      <div className="px-3 pb-4 pt-2 border-t border-border space-y-1">
        {/* Language toggle */}
        <button
          onClick={toggleLang}
          data-testid="lang-toggle"
          title={t.language.label}
          className={`w-full flex items-center gap-3 rounded-lg transition-colors text-muted-foreground hover:bg-muted hover:text-foreground ${collapsed ? "justify-center px-2 py-2.5" : "px-3 py-2.5"}`}
        >
          <Globe className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
          {!collapsed && <span className="text-sm truncate">{lang === "es" ? "English" : "Español"}</span>}
        </button>

        {user && !collapsed && (
          <Link
            to={user.username ? `/profile/${user.username}` : "/profile"}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted transition-colors"
          >
            <UserAvatar user={user} size="sm" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate text-foreground">{user.name?.split(" ")[0] || user.username}</p>
              <p className="text-xs text-muted-foreground truncate">@{user.username || "user"}</p>
            </div>
          </Link>
        )}
        {user && collapsed && (
          <Link to={user.username ? `/profile/${user.username}` : "/profile"} className="flex justify-center py-2" title={`@${user.username || user.name}`}>
            <UserAvatar user={user} size="sm" />
          </Link>
        )}

        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-button"
          title={collapsed ? t.nav.logout : undefined}
          className={`w-full gap-3 text-muted-foreground hover:text-destructive hover:bg-destructive/10 ${collapsed ? "justify-center px-2" : "justify-start px-3"}`}
        >
          <LogOut className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.8} />
          {!collapsed && <span className="text-sm">{t.nav.logout}</span>}
        </Button>
      </div>
    </aside>
  );
}
